import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import Icon from '../components/Icons';
import { fmtDate, formatINR, services, serviceOf } from '../data/common';
import { bookingStatus, statusLabel, useBookings } from './BookingsContext';
import { ModuleIcon } from './FlowParts';

export function AccountNav() {
  const { user } = useAuth();
  const { bookings } = useBookings();
  return (
    <aside className="account-nav panel">
      <div className="account-user">
        <span className="avatar"><Icon name="user" size={22} /></span>
        <div><b>Hi, {user?.firstName || 'Traveller'}</b>{user && <span className="muted small">{user.email}</span>}</div>
      </div>
      <nav>
        <NavLink end to="/my-trips" className={({ isActive }) => (isActive ? 'active' : '')}>
          <Icon name="briefcase" size={18} /> All trips {user && <span className="count">{bookings.length}</span>}
        </NavLink>
        {services.map((s) => (
          <NavLink key={s.key} to={`${s.path}/bookings`} className={({ isActive }) => (isActive ? 'active' : '')}>
            <Icon name={s.key} size={18} /> {s.label} {user && <span className="count">{bookings.filter((b) => b.module === s.key).length}</span>}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}

const tabs = [['upcoming', 'Upcoming'], ['held', 'On hold'], ['completed', 'Completed'], ['cancelled', 'Cancelled']];

export function BookingRow({ booking }) {
  const st = bookingStatus(booking);
  const s = serviceOf(booking.module);
  return (
    <Link to={`${s.path}/bookings/${booking.id}`} className="booking-row">
      <ModuleIcon module={booking.module} />
      <div className="booking-row-main">
        <div className="booking-row-top">
          <span className="muted small">{s.noun} · {booking.id}</span>
          <span className={`status status-${st}`}>{statusLabel[st]}</span>
        </div>
        <h3>{booking.title}</h3>
        <span className="muted small">{booking.subtitle}</span>
      </div>
      <div className="booking-row-meta">
        <span className="muted small">{booking.module === 'hotels' ? 'Check-in' : 'Travel date'}</span>
        <b>{fmtDate(booking.travelDate)}</b>
      </div>
      <div className="booking-row-meta right">
        <span className="muted small">{st === 'cancelled' ? 'Refund' : st === 'held' ? 'Quoted fare' : 'Amount paid'}</span>
        <b>{formatINR(st === 'cancelled' ? booking.refund?.amount || 0 : booking.fare.total)}</b>
      </div>
      <Icon name="right" size={18} className="booking-row-arrow" />
    </Link>
  );
}

/** Bookings list for one module, or for all modules when `module` is omitted. */
export default function BookingsListPage({ module, title }) {
  const { user } = useAuth();
  const { bookings } = useBookings();
  const [tab, setTab] = useState('upcoming');
  const mine = bookings.filter((b) => !module || b.module === module);
  const list = mine.filter((b) => bookingStatus(b) === tab).sort((a, b) => (tab === 'upcoming' ? a.travelDate.localeCompare(b.travelDate) : b.travelDate.localeCompare(a.travelDate)));
  const s = module && serviceOf(module);

  return (
    <div className="container account">
      <AccountNav />
      <div className="account-main">
        <div className="account-head">
          <div>
            <div className="crumbs"><Link to="/">Home</Link> <Icon name="right" size={12} /> <Link to="/my-trips">My Trips</Link>{s && <> <Icon name="right" size={12} /> <span>{s.label}</span></>}</div>
            <h1>{title}</h1>
          </div>
          {s && <Link to={s.path} className="btn btn-primary btn-sm">+ New {s.noun.toLowerCase()} booking</Link>}
        </div>
        {user && (
          <div className="underline-tabs account-tabs">
            {tabs.map(([k, l]) => (
              <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
                {l} <span className="tab-count">{mine.filter((b) => bookingStatus(b) === k).length}</span>
              </button>
            ))}
          </div>
        )}
        {!user ? (
          <div className="panel empty">
            <span className="empty-icon"><Icon name="briefcase" size={32} /></span>
            <h3>Sign in to see your trips</h3>
            <p className="muted">Your bookings appear here once you log in.</p>
            <Link to="/account/login" className="btn btn-primary">Sign in</Link>
          </div>
        ) : list.length ? (
          <div className="booking-list">{list.map((b) => <BookingRow key={b.id} booking={b} />)}</div>
        ) : (
          <div className="panel empty">
            <span className="empty-icon"><Icon name={module || 'briefcase'} size={32} /></span>
            <h3>No {tab} {s ? s.label.toLowerCase() : 'trips'}</h3>
            <p className="muted">{tab === 'upcoming' ? 'Looks like you have no trips planned. Time to explore!' : `You have no ${tab} bookings.`}</p>
            <Link to={s ? s.path : '/'} className="btn btn-primary">{s ? `Book ${s.label.toLowerCase()}` : 'Plan a trip'}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
